<template>
  <div class="settings-page">
    <h2>Dashboard</h2>
    <div class="subtitle">Customize your dashboard experience.</div>

    <section>
      <div class="entry vertical">
        <div class="label">
          <h3>Open/close dashboard</h3>
          <p class="description">
            Quickly open the dashboard from anywhere on your desktop computer.
          </p>
        </div>
        <GlobalHotkeyCapture
          v-if="inElectron"
          class="keyboard-shortcut"
          :hotkey-name="dashboardHotkeyIdentifier"
        />
        <BaseAlert v-else state="info" size="small">
          ⚠️ This hotkey can only be assigned in the
          <a
            class="download-link"
            href="https://routine.co/download"
            target="_blank"
            >desktop app</a
          >.
        </BaseAlert>
      </div>
      <div class="entry">
        <div class="preference-copy">
          <label>Tasks</label>
          <div class="note">
            Show today's tasks on the left side of the dashboard.
          </div>
        </div>
        <div
          ref="tasksToggleEl"
          @mouseenter="tasksTooltip.showTooltip"
          @mouseleave="tasksTooltip.hideTooltip"
        >
          <BaseToggleButton
            :checked="tasks && !isCalendarWidget"
            :disabled="isCalendarWidget"
            @change="(value) => (tasks = value)"
          />
        </div>
      </div>
      <div class="entry">
        <div class="preference-copy">
          <label>Timeline</label>
          <div class="note">
            Show the day's upcoming events on the right side of the dashboard.
          </div>
        </div>
        <div
          ref="timelineToggleEl"
          @mouseenter="timelineTooltip.showTooltip"
          @mouseleave="timelineTooltip.hideTooltip"
        >
          <BaseToggleButton
            :checked="timeline && !isCalendarWidget"
            :disabled="isCalendarWidget"
            @change="(value) => (timeline = value)"
          />
        </div>
      </div>
      <div class="entry">
        <div class="preference-copy">
          <label>Widget</label>
          <div class="note">
            Choose the widget displayed underneath the console.
          </div>
        </div>
        <BaseButtonGroup>
          <template #firstButton>
            <BaseButton
              kind="main"
              type="secondary"
              text="None"
              :state="widget === 'none' ? 'active' : 'default'"
              @click="widget = 'none'"
            />
          </template>
          <template #middleButtons>
            <BaseButton
              kind="main"
              type="secondary"
              text="Journal"
              :state="widget === 'journal' ? 'active' : 'default'"
              @click="selectJournalWidget"
            />
          </template>
          <template #lastButton>
            <BaseButton
              kind="main"
              type="secondary"
              text="Calendar"
              :state="widget === 'calendar' ? 'active' : 'default'"
              @click="widget = 'calendar'"
            />
          </template>
        </BaseButtonGroup>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onBeforeUnmount, ref } from "vue";

import BaseAlert from "@/components/base/BaseAlert.vue";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseButtonGroup from "@/components/base/BaseButtonGroup.vue";
import BaseToggleButton from "@/components/base/BaseToggleButton.vue";
import {
  DASHBOARD_TASKS_KEY,
  DASHBOARD_TIMELINE_KEY,
  DASHBOARD_WIDGET_KEY,
  DEFAULT_DASHBOARD_TASKS,
  DEFAULT_DASHBOARD_TIMELINE,
  DEFAULT_DASHBOARD_WIDGET,
  type DashboardWidget,
} from "@/components/dashboard/widget";
import GlobalHotkeyCapture from "@/components/GlobalHotkeyCapture.vue";
import * as inject from "@/components/inject";
import { PERSONAL_ORG_APP_SCREEN_IDS } from "@/components/menu/menuItems";
import { useTooltip, type TooltipContent } from "@/components/tooltip";
import { useBrowserStore } from "@/composables/useBrowserStore";
import { useReleaser } from "@/ref";
import { RouteNames } from "@/routes/types";
import { hotkeys } from "@/utils/hotkeys";
import { in_electron } from "@/utils/in";

export default defineComponent({
  components: {
    BaseAlert,
    BaseButton,
    BaseButtonGroup,
    BaseToggleButton,
    GlobalHotkeyCapture,
  },
  setup() {
    const inElectron = computed(() => in_electron);
    const menu = inject.menu();
    const profile = inject.profile();

    // Shared with the dashboard window through the browser store: it re-reads
    // the value every time it is shown (see windows/dashboard/App.vue).
    // Namespaced per account (adopting the values written un-namespaced by
    // older versions on first read).
    const releaser = useReleaser();
    const { migrate, syncState } = useBrowserStore(
      profile.value.user.accountId
    );
    migrate(DASHBOARD_WIDGET_KEY);
    migrate(DASHBOARD_TASKS_KEY);
    migrate(DASHBOARD_TIMELINE_KEY);
    const widget = releaser.add(
      syncState<DashboardWidget>(DASHBOARD_WIDGET_KEY, DEFAULT_DASHBOARD_WIDGET)
    );
    const tasks = releaser.add(
      syncState<boolean>(DASHBOARD_TASKS_KEY, DEFAULT_DASHBOARD_TASKS)
    );
    const timeline = releaser.add(
      syncState<boolean>(DASHBOARD_TIMELINE_KEY, DEFAULT_DASHBOARD_TIMELINE)
    );
    // The Journal widget only makes sense with the Journal screen reachable:
    // enabling it also unhides the Journal menu item (in case the user hid
    // it) so the "Open journal" link has somewhere to go.
    const selectJournalWidget = async () => {
      widget.value = "journal";
      if (menu.isRouteVisible(RouteNames.Journal)) return;
      const journalItem = menu.hiddenItems.value.find(
        (item) =>
          item.target.kind === "app_screen" &&
          item.target.value === PERSONAL_ORG_APP_SCREEN_IDS.Journal
      );
      if (journalItem) await menu.toggleAppScreenVisibility(journalItem);
    };

    // The calendar widget takes the whole dashboard width: the tasks list and
    // the timeline are forced off while it is selected. The stored
    // preferences are left untouched, so they come back when switching to
    // another widget.
    const isCalendarWidget = computed(() => widget.value === "calendar");

    // Explain the disabled toggles: no content outside the calendar widget,
    // so the tooltip only shows while they are actually disabled.
    const disabledNote = computed<TooltipContent>(() =>
      isCalendarWidget.value
        ? "Cannot be enabled with the Calendar widget"
        : undefined
    );
    const tasksToggleEl = ref<HTMLElement>();
    const tasksTooltip = useTooltip(tasksToggleEl, disabledNote, "top");
    const timelineToggleEl = ref<HTMLElement>();
    const timelineTooltip = useTooltip(timelineToggleEl, disabledNote, "top");
    onBeforeUnmount(() => {
      tasksTooltip.release();
      timelineTooltip.release();
    });
    return {
      isCalendarWidget,
      dashboardHotkeyIdentifier: hotkeys.STORE_IDENTIFIERS.DASHBOARD,
      inElectron,
      selectJournalWidget,
      tasks,
      tasksToggleEl,
      tasksTooltip,
      timeline,
      timelineToggleEl,
      timelineTooltip,
      widget,
    };
  },
});
</script>

<style scoped lang="scss">
@use "../../styles/settings.scss";

.download-link {
  // Same blue as the "New" badge ($tertiary-50) instead of the browser's
  // default link color.
  @include themed() {
    color: t($tertiary);
  }
}

.note {
  @extend %font-caption-regular;
  opacity: 0.75;
}
</style>
